// Schema-driven block editor. Given a `type` from BLOCK_REGISTRY and the
// current block, renders a form covering exactly the fields declared in
// the registry. New block types don't need new editor code — just add
// fields to the registry.

import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Plus, X, GripVertical, ChevronUp, ChevronDown } from "lucide-react";
import { API } from "../../App";
import { GenerateImageButton } from "./GenerateImageButton";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Button } from "../ui/button";
import { BLOCK_REGISTRY } from "../blocks/registry";
import { sendVideo, stageText } from "../videoSend/sendVideo";
import { readPrefs } from "../videoSend/prefs";
import { SendOptionsButton } from "../videoSend/SendOptions";
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, arrayMove, rectSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const uploadAsset = async (layoutKey, file) => {
  const fd = new FormData();
  fd.append("file", file);
  const r = await axios.post(
    `${API}/admin/page-layouts/${encodeURIComponent(layoutKey)}/upload`,
    fd,
    { withCredentials: true },
  );
  return r.data.url;
};

const VideoUploadField = ({ value, onChange, layoutKey }) => {
  const [progress, setProgress] = useState(null);
  const doUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setProgress({ phase: "read", pct: 0 });
    try {
      const url = (await sendVideo(file, { type: "block", layoutKey }, readPrefs(), setProgress))?.url || "";
      onChange(url);
      toast.success("Video uploaded");
    } catch (err) { toast.error(err?.response?.data?.detail || err.message || "Upload failed"); }
    finally { setProgress(null); e.target.value = ""; }
  };
  const src = value && value.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${value}` : value;
  return (
    <div className="flex items-center gap-3 mt-1">
      {value ? (
        <div className="relative">
          <video src={src} className="h-20 w-28 rounded-md border object-cover bg-black" muted />
          <button onClick={() => onChange("")} className="absolute -top-1 -right-1 bg-[#C0392B] text-white rounded-full p-0.5"><X className="h-3 w-3" /></button>
        </div>
      ) : (
        <div className="h-20 w-28 rounded-md border-2 border-dashed border-[#E2E0DC]" />
      )}
      <label className="text-sm text-[#2E5C55] font-semibold cursor-pointer inline-flex items-center gap-1">
        <Upload className="h-4 w-4" />
        {progress != null ? <span data-testid="block-video-stage">{stageText(progress) || "Starting…"}</span> : (value ? "Replace video" : "Upload video")}
        <input type="file" accept="video/mp4,video/quicktime,video/webm,video/*" className="hidden" onChange={doUpload} disabled={progress != null} />
      </label>
      <SendOptionsButton />
    </div>
  );
};

const AudioUploadField = ({ value, onChange, layoutKey }) => {
  const [busy, setBusy] = useState(false);
  const doUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 40 * 1024 * 1024) { toast.error("Audio is over 40MB"); e.target.value = ""; return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await axios.post(`${API}/admin/page-layouts/${encodeURIComponent(layoutKey)}/audio`, fd, { withCredentials: true });
      onChange(r.data.url);
      toast.success("Audio uploaded");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const src = value && value.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${value}` : value;
  return (
    <div className="mt-1 space-y-2">
      {value && <audio src={src} controls preload="none" className="w-full max-w-xs" />}
      <div className="flex items-center gap-3">
        <label className="text-sm text-[#2E5C55] font-semibold cursor-pointer inline-flex items-center gap-1">
          <Upload className="h-4 w-4" />
          {busy ? "Uploading…" : (value ? "Replace audio" : "Upload audio")}
          <input type="file" accept="audio/*,.mp3,.m4a,.wav,.ogg" className="hidden" onChange={doUpload} disabled={busy} />
        </label>
        {value && <button onClick={() => onChange("")} className="text-[#C0392B] inline-flex items-center gap-1 text-sm"><X className="h-3.5 w-3.5" /> Remove</button>}
      </div>
    </div>
  );
};

const shouldShow = (field, config) => {
  if (!field.dependsOn) return true;
  return config[field.dependsOn.key] === field.dependsOn.equals;
};

const SortableImage = ({ url, onRemove }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: url });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  return (
    <div ref={setNodeRef} style={style} className="relative">
      <img src={url} alt="" className="h-20 w-20 rounded-md border object-cover" />
      <button {...attributes} {...listeners} style={{ touchAction: "none" }}
        className="absolute bottom-0.5 left-0.5 bg-black/70 text-white rounded px-1 py-0.5 cursor-grab text-[9px] flex items-center gap-0.5"
        onClick={(e) => e.preventDefault()}>
        <GripVertical className="h-3 w-3" /> drag
      </button>
      <button onClick={() => onRemove(url)}
        className="absolute -top-1 -right-1 bg-[#C0392B] text-white rounded-full p-0.5">
        <X className="h-3 w-3" />
      </button>
    </div>
  );
};

const ImageField = ({ value, onChange, layoutKey }) => {
  const [busy, setBusy] = useState(false);
  const doUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const url = await uploadAsset(layoutKey, f);
      onChange(url);
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <div className="flex items-center gap-3">
      {value ? (
        <div className="relative">
          <img src={value} alt="" className="h-20 w-20 object-cover rounded-md border" />
          <button onClick={() => onChange("")} className="absolute -top-1 -right-1 bg-[#C0392B] text-white rounded-full p-0.5">
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <div className="h-20 w-20 rounded-md border-2 border-dashed border-[#E2E0DC]" />
      )}
      <label className="text-sm text-[#2E5C55] font-semibold cursor-pointer inline-flex items-center gap-1">
        <Upload className="h-4 w-4" />
        {busy ? "Uploading…" : (value ? "Replace" : "Upload")}
        <input type="file" accept="image/*" className="hidden" onChange={doUpload} disabled={busy} />
      </label>
      <GenerateImageButton onDone={onChange} purpose={layoutKey} />
    </div>
  );
};

const ImageListField = ({ value = [], onChange, layoutKey }) => {
  const [busy, setBusy] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const doUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadAsset(layoutKey, f));
      onChange([...(value || []), ...urls]);
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = value.indexOf(active.id);
    const newIdx = value.indexOf(over.id);
    onChange(arrayMove(value, oldIdx, newIdx));
  };
  return (
    <div className="flex flex-wrap gap-2">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={value} strategy={rectSortingStrategy}>
          {value.map((url) => (
            <SortableImage key={url} url={url} onRemove={(u) => onChange(value.filter((x) => x !== u))} />
          ))}
        </SortableContext>
      </DndContext>
      <label className="h-20 w-20 rounded-md border-2 border-dashed border-[#2E5C55]/40 flex flex-col items-center justify-center cursor-pointer text-[#2E5C55] text-[10px] font-semibold text-center leading-tight px-1">
        <Plus className="h-4 w-4" />
        {busy ? "…" : "Add pics"}
        <span className="text-[9px] font-normal">multi-select</span>
        <input type="file" accept="image/*" multiple className="hidden" onChange={doUpload} disabled={busy} />
      </label>
      <div className="h-20 w-20 rounded-md border-2 border-dashed border-[#7C3AED]/40 flex items-center justify-center text-center px-1"><GenerateImageButton compact onDone={(u) => onChange([...(value || []), u])} purpose={layoutKey} /></div>
    </div>
  );
};

// Multi-line body where each line is a paragraph / bullet: reorder or drop lines with ▲▼✕, edit the text below.
const LinesField = ({ value = "", onChange, rows = 4, testId = "lines" }) => {
  const ls = String(value || "").split("\n");
  const idxs = ls.map((l, i) => (l.trim() ? i : -1)).filter((i) => i >= 0);
  const set = (next) => onChange(next.join("\n"));
  const move = (pos, dir) => {
    const to = pos + dir;
    if (to < 0 || to >= idxs.length) return;
    const next = [...ls]; [next[idxs[pos]], next[idxs[to]]] = [next[idxs[to]], next[idxs[pos]]]; set(next);
  };
  const remove = (pos) => { if (!window.confirm("Remove this line?")) return; set(ls.filter((_, i) => i !== idxs[pos])); };
  const plain = (l) => l.replace(/\[([^\]]+)\]\([^)]+\)/g, "$1").replace(/\*\*|\*/g, "");
  return (
    <div className="mt-1 space-y-1.5" data-testid={testId}>
      {idxs.length > 1 && (
        <ol className="rounded-md border border-dashed border-[#E2E0DC] bg-[#FBFAF8] divide-y divide-[#EEECE8]">
          {idxs.map((li, pos) => (
            <li key={li} className="flex items-center gap-1.5 px-2 py-1 text-[12px] text-[#3a3a3a]">
              <span className="w-5 shrink-0 text-[10px] font-bold text-[#9a9a9a]">{pos + 1}</span>
              <span className="flex-1 truncate" title={plain(ls[li])}>{plain(ls[li])}</span>
              <button type="button" onClick={() => move(pos, -1)} disabled={pos === 0} className="text-[#5C5C5C] disabled:opacity-25" title="Move line up" data-testid={`${testId}-up-${pos}`}><ChevronUp className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => move(pos, 1)} disabled={pos === idxs.length - 1} className="text-[#5C5C5C] disabled:opacity-25" title="Move line down" data-testid={`${testId}-down-${pos}`}><ChevronDown className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => remove(pos)} className="text-[#C0392B]" title="Remove line" data-testid={`${testId}-remove-${pos}`}><X className="h-3.5 w-3.5" /></button>
            </li>
          ))}
        </ol>
      )}
      <Textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
};

const LinkListField = ({ value = [], onChange, itemFields, layoutKey, templates }) => {
  const add = () => {
    const empty = Object.fromEntries(itemFields.map((f) => [f.key, ""]));
    // Auto-slug the id field if present (announcement_ticker uses it as
    // dismiss-memory key; admin can rename it, but blank breaks dismiss).
    if ("id" in empty) empty.id = `ann_${Math.random().toString(36).slice(2, 10)}`;
    onChange([...(value || []), empty]);
  };
  const addFromTemplate = (tpl) => {
    const empty = Object.fromEntries(itemFields.map((f) => [f.key, ""]));
    if ("id" in empty) empty.id = `ann_${Math.random().toString(36).slice(2, 10)}`;
    onChange([...(value || []), { ...empty, ...(tpl.entry || {}) }]);
    toast.success(`Added "${tpl.label.replace(/^[^\s]+\s/, "")}" — edit the dates and details`);
  };
  const update = (idx, key, v) => {
    const next = [...value]; next[idx] = { ...next[idx], [key]: v }; onChange(next);
  };
  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= value.length) return;
    const next = [...value]; [next[idx], next[to]] = [next[to], next[idx]]; onChange(next);
  };
  return (
    <div className="space-y-2">
      {(value || []).map((item, idx) => (
        <div key={idx} className="border border-[#E2E0DC] rounded-md p-3 bg-white space-y-2 relative">
          <div className="absolute top-1 right-1 flex items-center gap-0.5">
            <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-[#5C5C5C] disabled:opacity-25" title="Move up" data-testid={`list-item-up-${idx}`}>
              <ChevronUp className="h-4 w-4" />
            </button>
            <button onClick={() => move(idx, 1)} disabled={idx === value.length - 1} className="text-[#5C5C5C] disabled:opacity-25" title="Move down" data-testid={`list-item-down-${idx}`}>
              <ChevronDown className="h-4 w-4" />
            </button>
            <button onClick={() => remove(idx)} className="text-[#C0392B]" title="Remove" data-testid={`list-item-remove-${idx}`}>
              <X className="h-4 w-4" />
            </button>
          </div>
          {itemFields.map((f) => (
            <div key={f.key}>
              <Label className="text-[11px] uppercase tracking-wider">{f.label}</Label>
              {f.kind === "image" ? (
                <ImageField value={item[f.key]} onChange={(v) => update(idx, f.key, v)} layoutKey={layoutKey} />
              ) : f.kind === "video" ? (
                <VideoUploadField value={item[f.key]} onChange={(v) => update(idx, f.key, v)} layoutKey={layoutKey} />
              ) : f.kind === "audio" ? (
                <AudioUploadField value={item[f.key]} onChange={(v) => update(idx, f.key, v)} layoutKey={layoutKey} />
              ) : f.kind === "textarea" ? (
                <Textarea rows={f.rows || 2} value={item[f.key] ?? ""} onChange={(e) => update(idx, f.key, e.target.value)} className="mt-1" />
              ) : f.kind === "lines" ? (
                <LinesField rows={f.rows || 4} value={item[f.key]} onChange={(v) => update(idx, f.key, v)} testId={`lines-${idx}`} />
              ) : f.kind === "datetime" ? (
                <Input type="datetime-local" value={item[f.key] ?? ""} onChange={(e) => update(idx, f.key, e.target.value)} className="mt-1" />
              ) : f.kind === "boolean" ? (
                <div className="mt-1"><Switch checked={!!item[f.key]} onCheckedChange={(v) => update(idx, f.key, v)} /></div>
              ) : f.kind === "color" ? (
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" value={item[f.key] || "#1B3A5C"} onChange={(e) => update(idx, f.key, e.target.value)} className="h-9 w-16 rounded border cursor-pointer" />
                  <Input value={item[f.key] || ""} onChange={(e) => update(idx, f.key, e.target.value)} className="w-32" />
                </div>
              ) : f.kind === "select" ? (
                <select
                  value={item[f.key] ?? f.options?.[0] ?? ""}
                  onChange={(e) => update(idx, f.key, e.target.value)}
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <Input
                  type={f.kind === "number" ? "number" : "text"}
                  step="any"
                  value={item[f.key] ?? ""}
                  onChange={(e) => update(idx, f.key, f.kind === "number" ? parseFloat(e.target.value) || null : e.target.value)}
                  className="mt-1"
                />
              )}
            </div>
          ))}
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={add} data-testid="linklist-add-blank">
          <Plus className="h-3 w-3 mr-1" /> Add blank row
        </Button>
        {(templates && templates.length > 0) && (
          <div className="relative group">
            <Button variant="outline" size="sm"
              className="border-[#2E5C55]/60 text-[#2E5C55]"
              data-testid="linklist-templates-btn">
              <Plus className="h-3 w-3 mr-1" /> Start from template ▾
            </Button>
            {/* Hover-menu keeps the picker one click away without opening a full modal. */}
            <div className="absolute z-30 mt-1 left-0 min-w-[240px] bg-white border border-[#E2E0DC] rounded-md shadow-lg overflow-hidden hidden group-focus-within:block group-hover:block">
              {templates.map((t) => (
                <button
                  key={t.label}
                  onClick={() => addFromTemplate(t)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-[#F9F5E9] border-b border-[#E2E0DC] last:border-b-0"
                  data-testid={`linklist-template-${t.label.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const ScheduleEditor = ({ value = [], onChange }) => {
  const addDay = () => onChange([...(value || []), { label: "", date: "", entries: [] }]);
  const updateDay = (i, k, v) => {
    const next = [...value]; next[i] = { ...next[i], [k]: v }; onChange(next);
  };
  const removeDay = (i) => onChange(value.filter((_, idx) => idx !== i));
  const addEntry = (di) => {
    const next = [...value];
    next[di] = { ...next[di], entries: [...(next[di].entries || []), { time: "", act: "", venue: "" }] };
    onChange(next);
  };
  const updateEntry = (di, ei, k, v) => {
    const next = [...value];
    const entries = [...(next[di].entries || [])];
    entries[ei] = { ...entries[ei], [k]: v };
    next[di] = { ...next[di], entries };
    onChange(next);
  };
  const removeEntry = (di, ei) => {
    const next = [...value];
    next[di] = { ...next[di], entries: next[di].entries.filter((_, i) => i !== ei) };
    onChange(next);
  };
  return (
    <div className="space-y-3">
      {(value || []).map((day, di) => (
        <div key={di} className="border border-[#E2E0DC] rounded-md p-3 bg-white">
          <div className="flex gap-2 mb-2">
            <Input placeholder="Day label (e.g. Saturday, Dec 20)" value={day.label} onChange={(e) => updateDay(di, "label", e.target.value)} />
            <Input placeholder="YYYY-MM-DD" value={day.date} onChange={(e) => updateDay(di, "date", e.target.value)} className="w-40" />
            <Button variant="ghost" size="sm" onClick={() => removeDay(di)}><X className="h-4 w-4" /></Button>
          </div>
          <div className="space-y-1">
            {(day.entries || []).map((e, ei) => (
              <div key={ei} className="flex gap-1 items-center">
                <Input placeholder="Time" value={e.time} onChange={(ev) => updateEntry(di, ei, "time", ev.target.value)} className="w-24" />
                <Input placeholder="Act / event" value={e.act} onChange={(ev) => updateEntry(di, ei, "act", ev.target.value)} />
                <Input placeholder="Venue" value={e.venue} onChange={(ev) => updateEntry(di, ei, "venue", ev.target.value)} className="w-48" />
                <Button variant="ghost" size="sm" onClick={() => removeEntry(di, ei)}><X className="h-3 w-3" /></Button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => addEntry(di)}>
              <Plus className="h-3 w-3 mr-1" /> Add entry
            </Button>
          </div>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addDay}>
        <Plus className="h-3 w-3 mr-1" /> Add day
      </Button>
    </div>
  );
};

const CsvScheduleField = ({ value, onDaysChange }) => {
  const parseCsv = (text) => {
    // Expected header: day,time,act,venue
    const rows = text.split(/\r?\n/).map((r) => r.trim()).filter(Boolean);
    if (!rows.length) return [];
    const header = rows[0].toLowerCase().split(",").map((h) => h.trim());
    const idx = (name) => header.indexOf(name);
    const dayIdx = idx("day"), timeIdx = idx("time"), actIdx = idx("act"), venueIdx = idx("venue");
    const dayMap = new Map();
    for (let i = 1; i < rows.length; i++) {
      const parts = rows[i].split(",").map((s) => s.trim());
      const label = parts[dayIdx] || "";
      if (!dayMap.has(label)) dayMap.set(label, { label, date: "", entries: [] });
      dayMap.get(label).entries.push({
        time: parts[timeIdx] || "",
        act: parts[actIdx] || "",
        venue: parts[venueIdx] || "",
      });
    }
    return Array.from(dayMap.values());
  };
  const parseJson = (text) => {
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) return parsed;
      if (parsed.days) return parsed.days;
    } catch { /* ignore */ }
    return null;
  };
  const doFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const text = await f.text();
    const days = f.name.endsWith(".json") ? parseJson(text) : parseCsv(text);
    if (!days) { toast.error("Couldn't parse — expected CSV (day,time,act,venue) or JSON array of days"); return; }
    onDaysChange(days);
    toast.success(`Loaded ${days.length} day(s)`);
    e.target.value = "";
  };
  return (
    <label className="inline-flex items-center gap-1.5 text-sm text-[#2E5C55] font-semibold cursor-pointer">
      <Upload className="h-4 w-4" />
      Upload CSV or JSON
      <input type="file" accept=".csv,.json,.txt" className="hidden" onChange={doFile} />
      <span className="text-[10px] text-[#5C5C5C] font-normal ml-2">CSV headers: day,time,act,venue</span>
    </label>
  );
};

const CoordsField = ({ value, onChange }) => {
  const [lat, lng] = Array.isArray(value) ? value : [null, null];
  const set = (i, v) => {
    const next = [lat, lng]; next[i] = v === "" ? null : parseFloat(v);
    onChange(next[0] != null || next[1] != null ? next : null);
  };
  return (
    <div className="flex gap-2">
      <Input type="number" step="any" placeholder="lat" value={lat ?? ""} onChange={(e) => set(0, e.target.value)} className="w-32" />
      <Input type="number" step="any" placeholder="lng" value={lng ?? ""} onChange={(e) => set(1, e.target.value)} className="w-32" />
    </div>
  );
};

export const BlockEditor = ({ block, layoutKey, onChange }) => {
  const meta = BLOCK_REGISTRY[block.type];
  const [local, setLocal] = useState(block);

  useEffect(() => { setLocal(block); }, [block.block_id]);

  const setField = (path, value) => {
    setLocal((prev) => {
      let next = { ...prev };
      if (path.startsWith("config.")) {
        const key = path.slice("config.".length);
        next.config = { ...(next.config || {}), [key]: value };
      } else {
        next[path] = value;
      }
      onChange(next);
      return next;
    });
  };

  if (!meta) {
    return <p className="text-sm text-[#C0392B]">Unknown block type: {block.type}</p>;
  }

  const cfg = local.config || {};

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-xs uppercase tracking-wider">Block title</Label>
        <Input value={local.title} onChange={(e) => setField("title", e.target.value)} className="mt-1" placeholder="Optional" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider">Motto / quip</Label>
        <Input value={local.quip} onChange={(e) => setField("quip", e.target.value)} className="mt-1" placeholder="Optional short line" />
      </div>

      <div className="flex flex-wrap gap-4 pt-2 border-t border-[#E2E0DC]">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <Switch checked={!!local.visible} onCheckedChange={(v) => setField("visible", v)} />
          <span className="text-sm">Visible</span>
        </label>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <Switch checked={!!local.collapsed_by_default} onCheckedChange={(v) => setField("collapsed_by_default", v)} />
          <span className="text-sm">Scan mode — show only Title + Quip (summary), rest under "Read more"</span>
        </label>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <Switch checked={!!local.in_mini_preview} onCheckedChange={(v) => setField("in_mini_preview", v)} />
          <span className="text-sm">Show in mini-preview</span>
        </label>
      </div>

      <div className="pt-4 border-t border-[#E2E0DC] space-y-4">
        <p className="text-xs text-[#5C5C5C] uppercase tracking-wider">{meta.label}</p>
        {meta.fields.map((f) => {
          if (!shouldShow(f, cfg)) return null;
          const v = cfg[f.key];
          const setV = (nv) => setField(`config.${f.key}`, nv);
          return (
            <div key={f.key}>
              <Label className="text-xs uppercase tracking-wider">{f.label}</Label>
              {f.kind === "text" || f.kind === "route" ? (
                <Input value={v ?? ""} onChange={(e) => setV(e.target.value)} className="mt-1" />
              ) : f.kind === "textarea" ? (
                <Textarea rows={f.rows || 4} value={v ?? ""} onChange={(e) => setV(e.target.value)} className="mt-1" />
              ) : f.kind === "number" ? (
                <Input type="number" step="any" value={v ?? ""} onChange={(e) => setV(e.target.value === "" ? null : parseFloat(e.target.value))} className="mt-1" />
              ) : f.kind === "boolean" ? (
                <div className="mt-1"><Switch checked={!!v} onCheckedChange={setV} /></div>
              ) : f.kind === "color" ? (
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" value={v || "#2E5C55"} onChange={(e) => setV(e.target.value)} className="h-9 w-16 rounded border cursor-pointer" />
                  <Input value={v || ""} onChange={(e) => setV(e.target.value)} className="w-32" />
                </div>
              ) : f.kind === "image" ? (
                <div className="mt-1"><ImageField value={v} onChange={setV} layoutKey={layoutKey} /></div>
              ) : f.kind === "image-list" ? (
                <div className="mt-1"><ImageListField value={v} onChange={setV} layoutKey={layoutKey} /></div>
              ) : f.kind === "coords" ? (
                <div className="mt-1"><CoordsField value={v} onChange={setV} /></div>
              ) : f.kind === "link-list" ? (
                <div className="mt-1"><LinkListField value={v} onChange={setV} itemFields={f.itemFields || []} layoutKey={layoutKey} templates={f.templates} /></div>
              ) : f.kind === "schedule-editor" ? (
                <div className="mt-1"><ScheduleEditor value={v} onChange={setV} /></div>
              ) : f.kind === "csv-schedule" ? (
                <div className="mt-1"><CsvScheduleField value={v} onDaysChange={(days) => setField("config.days", days)} /></div>
              ) : f.kind === "select" ? (
                <select value={v ?? f.options?.[0] ?? ""} onChange={(e) => setV(e.target.value)} className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid={`field-${f.key}`}>
                  {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <Input value={v ?? ""} onChange={(e) => setV(e.target.value)} className="mt-1" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BlockEditor;
